import React, { useEffect, useRef, useState } from 'react';
import { useStore, uid } from '../state/store';
import { Card, Chip, Banner, Empty, Text, Select, Check, SectionHead, Modal, Area, TermHelp } from '../components/ui';
import { exportSnapshot, importSnapshot, download, toCSV, CSV_TABLES } from '../lib/io';
import { SEED_TOP3 } from '../lib/seed';
import { api, loadSession, patchSession } from '../state/api';
import { UserAvatar } from '../components/UserAvatar';
import { ThemeSwitch } from '../components/UserMenu';
import { todayISO } from '../lib/dates';
import { resolveSprintBounds } from '../lib/derive';
import { applyRebaseline, CANONICAL_SPRINT_START, sprintStartOf } from '../lib/sprint';
import {
  assertCanRebaseline,
  emptyGateReviews,
  findPostMortemForSprint,
  withPostMortemPointer,
} from '../lib/sprintPostMortem';
import { SprintPostMortemForm } from '../components/Forms';
import { orgAgingMetrics, orgFunnelMetricLabels, orgFunnelMetrics, orgFunnelTargetsTitle, orgPrimaryDealEnabled } from '../lib/orgPlan';

function copyText(value) {
  if (!value || !navigator.clipboard?.writeText) return;
  navigator.clipboard.writeText(String(value)).catch(() => {});
}

function MetaRow({ label, value, mono = true, copyable = false }) {
  if (value == null || value === '') return null;
  return (
    <div className="profile-meta-row">
      <span className="label">{label}</span>
      <span className={`value ${mono ? '' : 'plain'}`}>
        {value}
        {copyable && (
          <button type="button" className="btn sm ghost" style={{ marginLeft: 8 }} onClick={() => copyText(value)}>
            Copy
          </button>
        )}
      </span>
    </div>
  );
}

function OrganizationCard({ me, loading }) {
  const org = me?.organization;
  return (
    <Card title="Organization" sub="Identity for this workspace">
      {loading && !me ? (
        <div className="dim tiny">Loading…</div>
      ) : (
        <div className="profile-meta">
          <MetaRow label="Name" value={org?.name} mono={false} />
          <MetaRow label="Organization ID" value={org?.id || me?.orgId} copyable />
          <MetaRow label="Your role" value={me?.memberRole} mono={false} />
          <MetaRow label="Created" value={org?.createdAt} />
          <MetaRow label="Updated" value={org?.updatedAt} />
        </div>
      )}
    </Card>
  );
}

function ProfileCard({ me, loading, onUpdated }) {
  const { state, set } = useStore();
  const fileRef = useRef(null);
  const [displayName, setDisplayName] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const theme = state.settings?.theme || 'system';

  useEffect(() => {
    if (me?.displayName) setDisplayName(me.displayName);
  }, [me?.displayName]);

  const saveName = async (e) => {
    e.preventDefault();
    setMsg('');
    setBusy(true);
    try {
      const next = await api.updateMe({ displayName });
      patchSession({
        displayName: next.displayName,
        profilePictureUrl: next.profilePictureUrl,
        organization: next.organization,
        memberRole: next.memberRole,
      });
      onUpdated?.(next);
      setMsg('Display name saved.');
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onPickFile = async (file) => {
    if (!file) return;
    setMsg('');
    setBusy(true);
    try {
      const type = file.type || 'image/jpeg';
      const next = await api.uploadProfilePicture(file, type);
      patchSession({
        displayName: next.displayName,
        profilePictureUrl: next.profilePictureUrl,
        organization: next.organization,
        memberRole: next.memberRole,
      });
      onUpdated?.(next);
      setMsg('Profile picture updated.');
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card title="Your profile" sub="Avatar and account details">
      {msg && <Banner tone={msg.includes('saved') || msg.includes('updated') ? 'ok' : 'bad'}>{msg}</Banner>}
      {loading && !me ? (
        <div className="dim tiny">Loading…</div>
      ) : (
        <>
          <div className="profile-avatar-row">
            <UserAvatar
              url={me?.profilePictureUrl}
              name={me?.displayName || me?.email || '?'}
              size={72}
            />
            <div className="stack gap-sm">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />
              <button
                type="button"
                className="btn sm"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                Upload photo
              </button>
              <div className="dim tiny">JPEG, PNG, or WebP · max 5 MB</div>
            </div>
          </div>

          <form className="stack gap-sm" onSubmit={saveName}>
            <Text label="Display name" value={displayName} onChange={setDisplayName} />
            <button type="submit" className="btn sm primary" disabled={busy || !displayName.trim()}>
              Save name
            </button>
          </form>

          <div className="hr" />
          <div className="stack gap-sm" style={{ marginBottom: 12 }}>
            <div className="dim tiny" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Appearance
            </div>
            <ThemeSwitch value={theme} onChange={(v) => set('settings.theme', v)} />
            <div className="dim tiny">Auto follows the operating system.</div>
          </div>

          <div className="profile-meta">
            <MetaRow label="Email" value={me?.email} mono={false} />
            <MetaRow label="User ID" value={me?.userId} copyable />
            <MetaRow label="Role" value={me?.memberRole} mono={false} />
            <MetaRow label="Status" value={me?.status} mono={false} />
          </div>
        </>
      )}
    </Card>
  );
}

function MembersCard() {
  const session = loadSession();
  const [members, setMembers] = useState([]);
  const [msg, setMsg] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('member');
  const canInvite = session?.memberRole === 'owner' || session?.memberRole === 'admin';

  const reload = async () => {
    try {
      const data = await api.listMembers();
      setMembers(data.members ?? []);
    } catch (err) {
      setMsg(err.message);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const invite = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      await api.inviteMember({ email, password, displayName, role });
      setEmail('');
      setPassword('');
      setDisplayName('');
      setMsg('Member invited.');
      await reload();
    } catch (err) {
      setMsg(err.message);
    }
  };

  return (
    <Card title="Organization members" sub={session?.organization?.name || session?.orgId}>
      {msg && <Banner tone={msg.includes('invited') ? 'ok' : 'bad'}>{msg}</Banner>}
      <div className="stack gap-sm" style={{ marginBottom: 12 }}>
        {members.map((m) => (
          <div key={m.id} className="row between" style={{ gap: 8 }}>
            <div>
              <div>{m.displayName || m.email}</div>
              <div className="dim tiny">{m.email}</div>
            </div>
            <Chip>{m.role}</Chip>
          </div>
        ))}
        {!members.length && <Empty>No members loaded.</Empty>}
      </div>
      {canInvite && (
        <form className="stack gap-sm" onSubmit={invite}>
          <Text label="Invite email" type="email" value={email} onChange={setEmail} />
          <Text label="Display name" value={displayName} onChange={setDisplayName} />
          <Text label="Temporary password" type="password" value={password} onChange={setPassword} />
          <Select
            label="Role"
            value={role}
            onChange={setRole}
            options={[
              { value: 'admin', label: 'Admin' },
              { value: 'member', label: 'Member' },
              { value: 'viewer', label: 'Viewer' },
            ]}
          />
          <button type="submit" className="btn sm primary">
            Invite member
          </button>
        </form>
      )}
    </Card>
  );
}

function AutopilotCard() {
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  const load = async () => {
    const res = await api.planStatus();
    setStatus(res);
  };

  useEffect(() => {
    load().catch((err) => setMsg(err.message));
  }, []);

  const save = async (enabled) => {
    setBusy('save');
    setMsg('');
    try {
      await api.putPlanAutopilot({ enabled });
      await load();
      setMsg(enabled ? 'Autopilot enabled.' : 'Autopilot paused.');
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  const restudy = async () => {
    setBusy('restudy');
    setMsg('');
    try {
      const res = await api.planRestudy({ trigger: 'manual', force: true });
      await load();
      if (res.planVersion?.rejected) {
        const errs = res.validationErrors || res.planVersion.validationErrors || [];
        setMsg(`Plan version rejected: ${errs.join('; ') || res.planVersion.why || 'policy validator'}.`);
      } else {
        setMsg(res.skipped ? `Skipped (${res.reason}).` : `Plan version ${res.planVersion?.id || 'saved'}.`);
      }
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <Card title="The Bridge — Autopilot" term="bridgeAutopilot" sub="Internal planning is autonomous. Background schedules stay off until this is enabled and a provider is configured. Unchanged state does not spend tokens.">
      {msg && <Banner tone={/rejected|failed|error/i.test(msg) ? 'bad' : /enabled|saved|Skipped|paused/i.test(msg) ? 'ok' : 'bad'}>{msg}</Banner>}
      {status?.lastError && !msg ? <Banner tone="bad">{status.lastError}</Banner> : null}
      <div className="row" style={{ marginBottom: 8 }}>
        <Chip tone={status?.freshness === 'fresh' ? 'green' : status?.freshness === 'stale' ? 'amber' : ''}>
          {status?.freshness === 'stale' ? 'PLAN STALE' : status?.freshness || 'idle'}
        </Chip>
        {status?.constraint?.title && <span className="dim tiny">{status.constraint.title}</span>}
      </div>
      <div className="dim tiny">Last studied {status?.lastStudiedAt || 'never'}</div>
      <div className="row tight" style={{ marginTop: 12 }}>
        <Check label="Enable autopilot" checked={!!status?.enabled} onChange={(on) => save(on)} />
        <button className="btn sm primary" disabled={!!busy} onClick={restudy}>
          {busy === 'restudy' ? 'Re-studying…' : 'Re-study now'}
        </button>
        <TermHelp term="restudy" label="Re-study" />
      </div>
    </Card>
  );
}

function AIProvidersCard() {
  const [providers, setProviders] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  const load = async () => {
    const res = await api.listBridgeProviders();
    const list = res.providers || [];
    setProviders(list);
    setDrafts((prev) => {
      const next = { ...prev };
      for (const p of list) {
        next[p.providerId] = {
          apiKey: next[p.providerId]?.apiKey || '',
          model: next[p.providerId]?.model || p.model || p.defaultModel,
          customModel: next[p.providerId]?.customModel || '',
        };
      }
      return next;
    });
  };

  useEffect(() => {
    load().catch((err) => setMsg(err.message));
  }, []);

  const save = async (p, extra = {}) => {
    setBusy(p.providerId);
    setMsg('');
    try {
      const draft = drafts[p.providerId] || {};
      const model = p.allowCustomModel && draft.customModel?.trim() ? draft.customModel.trim() : draft.model || p.model;
      const body = { model, ...extra };
      if (draft.apiKey?.trim()) body.apiKey = draft.apiKey.trim();
      await api.putBridgeProvider(p.providerId, body);
      setDrafts((d) => ({ ...d, [p.providerId]: { ...d[p.providerId], apiKey: '' } }));
      await load();
      setMsg(`${p.name} saved.`);
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  const test = async (p) => {
    setBusy(`test-${p.providerId}`);
    setMsg('');
    try {
      const res = await api.testBridgeProvider(p.providerId);
      setMsg(`${p.name} responded (${res.model}).`);
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <Card title="The Bridge — AI providers" sub="Enable one. Keys are write-only. Protocol and URL come from the application catalogue.">
      {msg && <Banner tone={/saved|responded/.test(msg) ? 'ok' : 'bad'}>{msg}</Banner>}
      {providers.length === 0 && <div className="dim tiny">No provider templates loaded yet.</div>}
      {providers.map((p) => {
        const draft = drafts[p.providerId] || { apiKey: '', model: p.model };
        const modelOptions = (p.models || []).map((m) => ({ value: m.id, label: m.label || m.id }));
        return (
          <div key={p.providerId} className="ai-provider-card">
            <div className="row" style={{ marginBottom: 8 }}>
              <strong>{p.name}</strong>
              <Chip>{p.protocol}</Chip>
              {p.hasKey && <Chip tone="green">Key ···{p.last4}</Chip>}
              {p.enabled && <Chip tone="green">Enabled</Chip>}
              <div className="spacer" />
              <Check
                label="Enable"
                checked={!!p.enabled}
                onChange={(on) => save(p, { enabled: on })}
              />
            </div>
            <div className="grid g2">
              <Text
                label="API key"
                type="password"
                value={draft.apiKey}
                onChange={(v) => setDrafts((d) => ({ ...d, [p.providerId]: { ...d[p.providerId], apiKey: v } }))}
                placeholder={p.hasKey ? `Stored ···${p.last4}` : 'Paste a key to store it'}
                help="Never shown again. Test uses the stored key."
              />
              <Select
                label="Model"
                value={draft.model}
                onChange={(v) => setDrafts((d) => ({ ...d, [p.providerId]: { ...d[p.providerId], model: v } }))}
                options={modelOptions.length ? modelOptions : [p.defaultModel]}
              />
            </div>
            {p.allowCustomModel && (
              <Text
                label="Custom model id"
                value={draft.customModel || ''}
                onChange={(v) => setDrafts((d) => ({ ...d, [p.providerId]: { ...d[p.providerId], customModel: v } }))}
                placeholder="Optional OpenRouter model id"
              />
            )}
            <div className="row tight" style={{ marginTop: 8 }}>
              <button className="btn sm primary" disabled={!!busy} onClick={() => save(p)}>
                {busy === p.providerId ? 'Saving…' : 'Save'}
              </button>
              <button className="btn sm" disabled={!!busy} onClick={() => test(p)}>
                {busy === `test-${p.providerId}` ? 'Testing…' : 'Test'}
              </button>
              <span className="dim tiny">{p.baseUrl}</span>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

function NexusCrmCard() {
  const [status, setStatus] = useState(null);
  const [draft, setDraft] = useState({ apiBaseUrl: '', nexusOrgId: '', apiKey: '', webBaseUrl: '' });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await api.getCrmStatus();
    setStatus(res);
    setDraft((prev) => ({
      apiBaseUrl: prev.apiBaseUrl || res.apiBaseUrl || '',
      nexusOrgId: prev.nexusOrgId || res.nexusOrgId || '',
      apiKey: '',
      webBaseUrl: prev.webBaseUrl || res.webBaseUrl || '',
    }));
  };

  useEffect(() => {
    load().catch((err) => setMsg(err.message));
  }, []);

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      const body = {
        apiBaseUrl: draft.apiBaseUrl.trim(),
        nexusOrgId: draft.nexusOrgId.trim(),
        webBaseUrl: draft.webBaseUrl.trim(),
      };
      if (draft.apiKey.trim()) body.apiKey = draft.apiKey.trim();
      const next = await api.putCrmConnection(body);
      setStatus(next);
      setDraft((d) => ({ ...d, apiKey: '' }));
      setMsg('Nexus connection saved.');
    } catch (err) {
      setMsg(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Nexus connection"
      sub="Optional CRM sync. Manual add and CSV remain first-class — especially for consulting tenants. Keys are write-only; Bridge syncs founder-thin shells and does not replace the CRM."
    >
      {msg && <Banner tone={/saved/.test(msg) ? 'ok' : 'bad'}>{msg}</Banner>}
      <div className="grid g2">
        <Text
          label="API base URL"
          value={draft.apiBaseUrl}
          onChange={(v) => setDraft((d) => ({ ...d, apiBaseUrl: v }))}
          placeholder="https://api.aipoio.xzero.media"
          help="Default host may still use *.aipoio.* until Nexus DNS changes."
        />
        <Text
          label="Web base URL"
          value={draft.webBaseUrl}
          onChange={(v) => setDraft((d) => ({ ...d, webBaseUrl: v }))}
          placeholder="https://aipoio.xzero.media"
          help="Used for View all in CRM."
        />
        <Text
          label="Nexus org ID"
          value={draft.nexusOrgId}
          onChange={(v) => setDraft((d) => ({ ...d, nexusOrgId: v }))}
          placeholder="idt_…"
          help="Nexus identity org id (idt_…). Must match the org your API key is issued for — not your Bridge45 org ID."
        />
        <Text
          label="API key"
          type="password"
          value={draft.apiKey}
          onChange={(v) => setDraft((d) => ({ ...d, apiKey: v }))}
          placeholder={status?.hasKey ? `Stored ···${status.last4}` : 'Paste API key…'}
          help="Never shown again. Issued under Nexus org settings."
        />
      </div>
      <div className="row tight" style={{ marginTop: 8 }}>
        <button className="btn sm primary" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save connection'}
        </button>
        {status?.hasKey && <Chip tone="green">Key ···{status.last4}</Chip>}
        {status?.connected && <Chip tone="green">Connected</Chip>}
      </div>
    </Card>
  );
}

function JiraIntegrationCard() {
  const { state, set } = useStore();
  const baseUrl = state.settings?.jira?.baseUrl || '';

  return (
    <Card
      title="Jira"
      sub="Base URL for outbound epic links. Bridge45 does not sync issues, statuses, or stories."
    >
      <Text
        label="Jira base URL"
        value={baseUrl}
        onChange={(v) => set('settings.jira.baseUrl', String(v || '').trim().replace(/\/+$/, ''))}
        placeholder="https://your-org.atlassian.net"
        help="Used on the Strategy Map to open each epic in Jira as /browse/{spaceKey}-{jiraId}."
      />
    </Card>
  );
}

export default function Settings() {
  const { state, set, add, patch, remove, replaceAll, reset, today, d } = useStore();
  const fileRef = useRef(null);
  const [msg, setMsg] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);
  const [rebaselineOpen, setRebaselineOpen] = useState(false);
  const [rebaselineDate, setRebaselineDate] = useState(CANONICAL_SPRINT_START);
  const [rebaselineReason, setRebaselineReason] = useState('');
  const [postMortemOpen, setPostMortemOpen] = useState(false);
  const [rebaselineBlock, setRebaselineBlock] = useState('');
  const sprint = d?.sprint || resolveSprintBounds(state);
  const endingT0 = sprintStartOf(state);
  const priorPm = findPostMortemForSprint(state, endingT0);
  const canRebaseline = assertCanRebaseline(state, endingT0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMeLoading(true);
      try {
        const data = await api.getMe();
        if (!cancelled) {
          setMe(data);
          patchSession({
            displayName: data.displayName,
            profilePictureUrl: data.profilePictureUrl,
            organization: data.organization,
            memberRole: data.memberRole,
            userId: data.userId,
            email: data.email,
          });
        }
      } catch (err) {
        if (!cancelled) setMsg(err.message);
      } finally {
        if (!cancelled) setMeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const doImport = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        replaceAll(importSnapshot(String(reader.result)));
        setMsg('Snapshot imported. All state replaced.');
      } catch (err) {
        setMsg(`Import failed: ${err.message}`);
      }
    };
    reader.readAsText(file);
  };

  const seedTop3 = () => {
    SEED_TOP3.forEach((t, i) =>
      add('top3', {
        id: uid('t3'),
        date: today,
        createdAt: today,
        text: t.text,
        expectedEvidence: t.expectedEvidence,
        workstream: i === 2 ? 'Saudi' : i === 1 ? 'D001' : 'Dubai',
        priority: 'High',
        status: 'Open',
        dueDate: today,
        owner: 'Founder',
        gateRelevance: '15',
        selectedForToday: true,
        todayRank: i + 1,
        completionResult: '',
        evidenceCreated: '',
        outcome: '',
        outcomeNote: '',
      })
    );
    setMsg('Suggested first three founder moves added to the queue and pinned to Today.');
  };

  const commitRebaseline = () => {
    const start = String(rebaselineDate || '').slice(0, 10);
    const reason = String(rebaselineReason || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !reason) return;
    const gate = assertCanRebaseline(state, endingT0);
    if (!gate.ok) {
      setRebaselineBlock(gate.error);
      setPostMortemOpen(true);
      return;
    }
    setRebaselineBlock('');
    const priorStart = endingT0;
    const postMortemId = gate.postMortem?.id || '';
    const decisionId = uid('dec');
    add('decisions', {
      id: decisionId,
      date: today,
      type: 'Sprint re-baseline',
      question: `Change Sprint Initialization T0 from ${priorStart} to ${start}?`,
      options: `Keep ${priorStart}; re-baseline to ${start}.`,
      evidence: `Post-mortem ${postMortemId}. Founder-attributed re-baseline. Gate dates Day 15/30/45 derive from the new T0.`,
      decision: reason,
      reviewDate: start,
      postMortemId,
    });
    let next = applyRebaseline(state, {
      start,
      reason,
      decisionId,
      by: state.settings?.founderName || 'Founder',
      today,
    });
    next = withPostMortemPointer(next, { postMortemId, priorSprintStart: priorStart });
    set('settings', { ...state.settings, ...next });
    // Reset live gate checkboxes for the new cycle only after archive exists.
    set('gateReviews', emptyGateReviews());
    setRebaselineOpen(false);
    setRebaselineReason('');
    setMsg(`Sprint re-baselined to ${start}. Prior post-mortem ${postMortemId} linked. Decision ${decisionId} recorded.`);
  };

  return (
    <>
      <div className="page-head">
        <h2>Settings</h2>
        <div className="sub">State is stored by the Bridge45 API (DynamoDB in production; local/memory for demos). Theme preference is kept in this browser only.</div>
      </div>

      {msg && <Banner tone="ok">{msg}</Banner>}

      <section className="section grid g2">
        <ProfileCard me={me} loading={meLoading} onUpdated={setMe} />
        <OrganizationCard me={me} loading={meLoading} />
      </section>

      <section className="section">
        <AIProvidersCard />
      </section>

      <section className="section">
        <AutopilotCard />
      </section>

      <section className="section">
        <NexusCrmCard />
      </section>

      <section className="section">
        <JiraIntegrationCard />
      </section>

      <section className="section grid g2">
        <MembersCard />
        <Card title="Operating">
          <Text label="Founder name" value={state.settings.founderName} onChange={(v) => set('settings.founderName', v)} />
          <Select label="Currency" value={state.settings.currency} onChange={(v) => set('settings.currency', v)} options={['AED', 'SAR', 'USD', 'EUR', 'GBP']} />
          <Check
            label="Show contextual help icons"
            checked={state.settings.showTermHelp !== false}
            onChange={(v) => set('settings.showTermHelp', v)}
            help="Show the (i) explanations beside Bridge45-specific terms. Turn them off once the operating model is familiar."
          />
          <div className="hr" />
          <div className="field">
            <label className="field-label">
              <span>Sprint Initialization (T0)</span>
              <TermHelp term="sprintT0" label="Sprint Initialization (T0)" />
            </label>
            <div className="value" style={{ fontWeight: 650 }}>{state.settings?.sprint?.start || state.settings.sprintStart || todayISO()}</div>
            <div className="help">
              Recorded start. Day 15, 30, and 45 are derived from this date. Changing T0 after the sprint begins requires an attributed Re-baseline Decision.
            </div>
          </div>
          <div className="row" style={{ marginTop: 8, gap: 8 }}>
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                setRebaselineDate(state.settings?.sprint?.start || CANONICAL_SPRINT_START);
                setRebaselineReason('');
                setRebaselineBlock('');
                setRebaselineOpen(true);
              }}
            >
              Re-baseline sprint
            </button>
            <TermHelp term="rebaseline" label="Re-baseline sprint" />
          </div>
          {!canRebaseline.ok && (
            <div className="dim tiny" style={{ marginTop: 8 }}>
              Post-mortem for T0 {endingT0} is required before re-baselining.
            </div>
          )}
          {state.settings?.sprint?.lastPostMortemId && (
            <div className="dim tiny" style={{ marginTop: 8 }}>
              Last linked post-mortem: {state.settings.sprint.lastPostMortemId}
              {state.settings.sprint.lastPostMortemSprintStart
                ? ` (prior T0 ${state.settings.sprint.lastPostMortemSprintStart})`
                : ''}
            </div>
          )}
          <div className="dim tiny" style={{ marginTop: 8 }}>
            {sprint.label}. Day 15 → {sprint.gates?.[0]?.date}, Day 30 → {sprint.gates?.[1]?.date}, Day 45 → {sprint.end}.
            {sprint.integrity?.rebaselined ? ` ${sprint.lockLabel}.` : ''}
          </div>
          <div className="hr" />
          <Check
            label="Use the real clock"
            checked={state.settings.useRealClock !== false}
            onChange={(v) => set('settings.useRealClock', v)}
            help="Turn this off to review a past day or rehearse a gate. The sprint day number follows the pinned date."
          />
          {state.settings.useRealClock === false && (
            <Text label="Pinned date" type="date" value={state.settings.pinnedDate} onChange={(v) => set('settings.pinnedDate', v)} />
          )}
        </Card>

        <Card title="Alert thresholds">
          <div className="grid g2">
            <Text label="Idle proposal (business days)" type="number" value={state.settings.alertThresholds.idleProposalDays} onChange={(v) => set('settings.alertThresholds.idleProposalDays', Number(v) || 0)} term="idleProposalDays" />
            <Text label="Idle Tier-A (business days)" type="number" value={state.settings.alertThresholds.idleTierADays} onChange={(v) => set('settings.alertThresholds.idleTierADays', Number(v) || 0)} term="idleTierADays" />
            <Text label="Runway watch (months)" type="number" value={state.settings.alertThresholds.runwayWatchMonths} onChange={(v) => set('settings.alertThresholds.runwayWatchMonths', Number(v) || 0)} term="runwayWatchMonths" />
            <Text label="Runway critical (months)" type="number" value={state.settings.alertThresholds.runwayCriticalMonths} onChange={(v) => set('settings.alertThresholds.runwayCriticalMonths', Number(v) || 0)} term="runwayCriticalMonths" />
            <Text label={`Material spend (${state.settings.currency})`} type="number" value={state.settings.alertThresholds.materialSpend} onChange={(v) => set('settings.alertThresholds.materialSpend', Number(v) || 0)} term="materialSpend" />
          </div>
        </Card>
      </section>

      {/* ------------------------------------------- funnel operating model */}
      <section className="section grid g2">
        <Card
          title={orgFunnelTargetsTitle(state) || 'Funnel targets'}
          sub="Operating hypothesis — adjust from evidence after Day 15"
        >
          <div className="grid g3">
            {(orgFunnelMetrics(state).length
              ? orgFunnelMetrics(state).map((m) => [m.key, m.label, m.term || m.key])
              : [
                  ['universe', 'Market universe', 'marketUniverse'],
                  ['qualifiedTargets', 'Qualified targets', 'qualifiedTargets'],
                  ['tierA', 'Active Tier A', 'activeTierA'],
                  ['conversations', 'Substantive conversations', 'substantiveConversations'],
                  ['qualifiedOpportunities', 'Qualified opportunities', 'qualifiedOpportunities'],
                ]
            ).map(([key, label, term]) => (
              <Text
                key={key}
                label={orgFunnelMetricLabels(state)[key] || label}
                type="number"
                term={term}
                value={state.settings.funnelTargets?.[key] ?? 0}
                onChange={(v) => set(`settings.funnelTargets.${key}`, Number(v) || 0)}
              />
            ))}
          </div>
          <Banner>
            These are hypotheses about how much raw material the sprint needs — not benchmarks, and not
            targets to be gamed. If the evidence after Day 15 says the ratios are different, change them here.
          </Banner>
        </Card>

        <Card title="Pipeline aging thresholds" sub="Business days before an item is called stale">
          <div className="grid g2">
            {(orgAgingMetrics(state).length
              ? orgAgingMetrics(state).map((m) => [m.key, m.label, m.term || m.key])
              : [
                  ['tierAContacted', 'Tier A contacted, no movement', 'agingTierA'],
                  ['discoveryNextAction', 'Discovery done, no next action', 'agingDiscovery'],
                  ['qualifiedOpportunity', 'Qualified opportunity static', 'agingQualified'],
                ]
            ).map(([key, label, term]) => (
              <Text
                key={key}
                label={label}
                type="number"
                term={term}
                value={state.settings.agingThresholds?.[key] ?? 0}
                onChange={(v) => set(`settings.agingThresholds.${key}`, Number(v) || 0)}
              />
            ))}
          </div>
        </Card>
      </section>

      {/* ------------------------------------------------------ approvals -- */}
      <section className="section">
        <SectionHead
          title="Regulator-sensitive communications"
          hint="Anything that goes to a regulator, or describes a regulated activity, waits here until approved."
          right={
            <button
              className="btn sm"
              onClick={() => add('approvals', { id: uid('ap'), date: today, label: '', audience: '', status: 'Pending', note: '' })}
            >
              Hold a communication
            </button>
          }
        />
        {(state.approvals || []).length === 0 ? (
          <Empty title="Nothing held for approval.">
            Stealth GTM means the founder decides what leaves the building, on purpose rather than by accident.
          </Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 95 }}>Date</th>
                    <th>Communication</th>
                    <th style={{ width: 180 }}>Audience</th>
                    <th style={{ width: 150 }}>Status</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {state.approvals.map((a) => (
                    <tr key={a.id} style={a.status === 'Pending' ? { background: 'var(--amber-bg)' } : undefined}>
                      <td className="num">{a.date}</td>
                      <td><input type="text" value={a.label} placeholder="What is being sent?" onChange={(e) => patch('approvals', a.id, { label: e.target.value })} /></td>
                      <td><input type="text" value={a.audience} placeholder="Regulator, partner…" onChange={(e) => patch('approvals', a.id, { audience: e.target.value })} /></td>
                      <td>
                        <select value={a.status} onChange={(e) => patch('approvals', a.id, { status: e.target.value })}>
                          {['Pending', 'Approved', 'Sent', 'Withdrawn'].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="actions">
                        <button className="btn sm ghost" onClick={() => remove('approvals', a.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {/* --------------------------------------------------------- data ---- */}
      <section className="section grid g2">
        <Card title="Export">
          <p className="muted tiny">Snapshots are plain JSON. Keep one before any risky change.</p>
          <div className="row tight" style={{ marginBottom: 12 }}>
            <button
              className="btn primary"
              onClick={() => download(`bridge45-snapshot-${today}.json`, exportSnapshot(state), 'application/json')}
            >
              Export snapshot (JSON)
            </button>
          </div>
          <div className="hr" />
          <p className="muted tiny">Individual tables as CSV:</p>
          <div className="row tight">
            {Object.entries(CSV_TABLES).map(([key, t]) => {
              const noPrimary = !orgPrimaryDealEnabled(state);
              const label =
                noPrimary && key === 'accounts'
                  ? state.planDefinition?.uiCopy?.exportAccountsLabel || 'Accounts'
                  : noPrimary && key === 'opportunities'
                    ? 'Opportunities'
                    : noPrimary && key === 'assets'
                      ? 'Asset candidates (unused)'
                      : noPrimary && key === 'saudi'
                        ? 'Secondary market opportunities'
                        : t.label;
              if (noPrimary && (key === 'assets' || key === 'saudi')) return null;
              return (
                <button
                  key={key}
                  className="btn sm"
                  onClick={() => download(`bridge45-${key}-${today}.csv`, toCSV(t.get(state), t.columns), 'text/csv')}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Card>

        <Card title="Import">
          <p className="muted tiny">
            Importing a snapshot replaces everything currently in the app. Missing fields are filled from the seed, so
            older snapshots still load.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={(e) => e.target.files[0] && doImport(e.target.files[0])}
          />
          <div className="row tight">
            <button className="btn" onClick={() => fileRef.current && fileRef.current.click()}>Import snapshot (JSON)</button>
            <button className="btn" onClick={seedTop3}>Load suggested first Top 3</button>
          </div>
          <div className="hr" />
          <p className="muted tiny">
            Opportunity CSV import lives on the Infrastructure page. Jira epic links (space key + issue id) are set on the
            45-Day Strategy Map; the Jira base URL is configured above.
          </p>
        </Card>
      </section>

      <section className="section">
        <SectionHead title="Danger" />
        <Card>
          <div className="row">
            <div style={{ flex: 1 }}>
              <strong>Reset everything</strong>
              <div className="dim tiny">Clears all recorded state and returns Bridge45 to the Day 1 seed.</div>
            </div>
            <button className="btn danger" onClick={() => setConfirmReset(true)}>Reset</button>
          </div>
        </Card>
      </section>

      <section className="section">
        <Card title="About">
          <div className="dim tiny">
            Bridge45 · state lives in DynamoDB behind the Fastify API at{' '}
            <span className="mono">/api/v1</span> — accounts, opportunities, assets, evidence, expenses, decisions and
            reviews as individual records, configuration as documents. Every write is audited. Bridge45 syncs
            founder-thin account shells from Nexus; it does not replace the CRM. No email, SMS,
            accounting, KYC, token issuance, wallet, or trading. Jira is link-only (base URL + epic space key and
            id); there is no issue synchronisation. It is a governance, accountability, evidence and
            founder-prioritisation application.
          </div>
        </Card>
      </section>

      {rebaselineOpen && (
        <Modal
          title="Re-baseline sprint"
          sub="This writes an attributed Decision, then moves T0. Every Day 15/30/45 date will derive from the new start. A complete sprint post-mortem for the ending T0 is required."
          onClose={() => setRebaselineOpen(false)}
          footer={
            <>
              <button className="btn" onClick={() => setRebaselineOpen(false)}>Cancel</button>
              <button
                className="btn primary"
                disabled={!rebaselineReason.trim() || !rebaselineDate || !canRebaseline.ok}
                onClick={commitRebaseline}
              >
                Record re-baseline
              </button>
            </>
          }
        >
          {rebaselineBlock ? <Banner tone="stop">{rebaselineBlock}</Banner> : null}

          {priorPm && canRebaseline.ok ? (
            <Card title="Prior sprint lessons" sub={`T0 ${priorPm.sprintStart}`}>
              <dl className="kv">
                <div><dt>Proved / killed</dt><dd>{priorPm.proved || '—'}</dd></div>
                <div><dt>Biggest miss</dt><dd>{priorPm.biggestMiss || '—'}</dd></div>
                <div><dt>Carry-forward</dt><dd>{priorPm.carryForward || '—'}</dd></div>
                <div>
                  <dt>Run again?</dt>
                  <dd>
                    {priorPm.runAgain === true ? 'Yes' : priorPm.runAgain === false ? 'No' : '—'}
                    {priorPm.runAgainWhy ? ` — ${priorPm.runAgainWhy}` : ''}
                  </dd>
                </div>
              </dl>
              <button
                type="button"
                className="btn sm ghost"
                style={{ marginTop: 8 }}
                onClick={() =>
                  setRebaselineReason(
                    (priorPm.carryForward || '').trim()
                      || `Carry-forward from ${priorPm.sprintStart}: ${priorPm.rationale || ''}`.trim(),
                  )
                }
              >
                Seed rationale from carry-forward
              </button>
            </Card>
          ) : (
            <Banner tone="amber">
              No complete post-mortem for T0 {endingT0}.{' '}
              <button type="button" className="btn sm" onClick={() => setPostMortemOpen(true)}>
                Complete early cycle-close
              </button>
            </Banner>
          )}

          <Text label="New Day 1 (T0)" type="date" value={rebaselineDate} onChange={setRebaselineDate} />
          <Area
            label="Rationale"
            value={rebaselineReason}
            onChange={setRebaselineReason}
            rows={4}
            placeholder="Required. Why the recorded Sprint Initialization must change."
          />
        </Modal>
      )}

      {postMortemOpen && (
        <SprintPostMortemForm
          earlyClose
          endedBy="rebaseline"
          onClose={() => setPostMortemOpen(false)}
          onSaved={() => {
            setPostMortemOpen(false);
            setRebaselineBlock('');
            setMsg(`Post-mortem saved for T0 ${endingT0}. You can re-baseline now.`);
          }}
        />
      )}

      {confirmReset && (
        <Modal
          title="Reset everything?"
          onClose={() => setConfirmReset(false)}
          footer={
            <>
              <button className="btn" onClick={() => setConfirmReset(false)}>Cancel</button>
              <button className="btn danger" onClick={() => { reset(); setConfirmReset(false); setMsg('State reset to Day 1 seed.'); }}>
                Reset
              </button>
            </>
          }
        >
          <Banner tone="stop">
            This deletes every evidence record, expense, decision and review — on the server, not just in this browser.
            Export a snapshot first if there is any doubt.
          </Banner>
        </Modal>
      )}
    </>
  );
}
